import { Controller } from "@hotwired/stimulus"
import { MediaFormMixin } from "./mixins/media_form_mixin"

// Controller for event form with media upload support
// Uses same batch-upload approach as post-form: server-rendered existing + addLocalPreview for new
export default class extends MediaFormMixin(Controller) {
  static targets = ["form", "mediaInput", "mediaPreview", "mediaIdsContainer"]
  static values = { maxMedias: { type: Number, default: 3 } }

  connect() {
    this.selectedFiles = []
    this.uploadedMediaIds = []

    // Initialize existing media IDs from hidden inputs (server-rendered)
    if (this.hasMediaPreviewTarget) {
      const existingInputs = this.mediaPreviewTarget.querySelectorAll('input[name="existing_media_ids[]"]')
      this.uploadedMediaIds = Array.from(existingInputs).map((input) => input.value)
    }

    this.isUploading = false
    this.setupWebcamListener()
  }

  handleWebcamCaptured(event) {
    const { file } = event.detail
    if (file) {
      this.selectedFiles.push(file)
      this.addLocalPreview(file)
    }
  }

  uploadMedia(event) {
    const files = Array.from(event.target.files)
    if (files.length === 0) return

    const remainingSlots = this.maxMediasValue - (this.selectedFiles.length + this.uploadedMediaIds.length)
    const filesToAdd = files.slice(0, Math.max(0, remainingSlots))

    filesToAdd.forEach((file) => {
      this.selectedFiles.push(file)
      this.addLocalPreview(file)
    })

    event.target.value = ""
  }

  addLocalPreview(file) {
    const index = this.uploadedMediaIds.length + this.selectedFiles.length - 1
    const { element: preview } = this.buildLocalPreviewElement(file, {
      removeAction: "event-form#removeMedia",
      fileIndex: index,
      size: "md"
    })
    preview.dataset.fileId = `file_${Date.now()}_${Math.random()}`
    this.mediaPreviewTarget.appendChild(preview)
    this.updateMediaPreviewVisibility()
  }

  removeMedia(event) {
    const mediaId = event.currentTarget.dataset.mediaId
    const fileIndex = event.currentTarget.dataset.fileIndex

    if (mediaId) {
      this.uploadedMediaIds = this.uploadedMediaIds.filter((id) => id !== mediaId)
      const preview = event.currentTarget.closest("[data-media-id]")
      if (preview) {
        preview.remove()
      }
      this.updateMediaPreviewVisibility()
    } else if (fileIndex !== undefined) {
      const index = parseInt(fileIndex, 10) - this.uploadedMediaIds.length
      const preview = this.mediaPreviewTarget.querySelector(`[data-file-id][data-file-index="${fileIndex}"]`)
      if (preview) {
        if (preview.dataset.objectUrl) {
          URL.revokeObjectURL(preview.dataset.objectUrl)
        }
        preview.remove()
      }
      this.selectedFiles.splice(index, 1)
      this.mediaPreviewTarget.querySelectorAll("[data-file-id]").forEach((el, i) => {
        const newIndex = this.uploadedMediaIds.length + i
        el.dataset.fileIndex = newIndex
        const btn = el.querySelector("button[data-file-index]")
        if (btn) btn.dataset.fileIndex = newIndex
      })
      this.updateMediaPreviewVisibility()
    }
  }

  async handleSubmit(event) {
    if (this.isUploading) return

    if (this.selectedFiles.length > 0) {
      event.preventDefault()
      this.isUploading = true

      try {
        const uploadedMediaIds = await this.uploadMediaFiles(this.selectedFiles)

        uploadedMediaIds.forEach((id) => {
          const input = document.createElement("input")
          input.type = "hidden"
          input.name = "media_ids[]"
          input.value = id
          this.mediaIdsContainerTarget.appendChild(input)
        })

        this.selectedFiles = []
        this.formTarget.requestSubmit()
      } catch (error) {
        console.error("Upload error:", error)
        alert("Upload error. Please check your connection.")
        this.isUploading = false
        this.element.dispatchEvent(new CustomEvent("submit-with-spinner:reset", { bubbles: true }))
      }
    }
  }

  disconnect() {
    this.revokeObjectUrls()
    this.disconnectWebcamListener()
  }
}
